import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';
import { SettingsService } from 'services/settings.service';
import { DEFAULT_SCAN_MODEL, ModelOption, ScanMode, parseScanConfig, resolveModelKey } from 'services/scan-config';

/**
 * The scan-model choice for one AI scanner page.
 *
 * Every AI scanner (meal photo, food label, equipment, recipe, workout plan)
 * reads the same `ScanProvider` config key, turns it into a ScanConfig, and
 * offers the shared `cs-scanner-model-picker` when that config puts it in 'any'
 * mode. Each page used to hold its own copy of that state and of the two methods
 * below; the copies had drifted, and nobody could tell which differences were
 * deliberate (deferred finding df-d3b3b23b9d34).
 *
 * A page owns one of these and calls it. It is deliberately NOT a base class:
 * `design-docs/scanner-shared-components.md` ("Decision: no ScannerPageBase")
 * rejected inheritance here because it would hide the pages' genuinely different
 * analyze flows. Composition shares the identical parts and hides nothing.
 *
 * The template reads `scanMode`, `modelOptions` and `selectedModel` through the
 * page's field, so all of those stay public.
 */
export class ScannerModelSelection {
  /** which selection mode the config puts this scanner in */
  scanMode: ScanMode = ScanMode.Fixed;
  /** the pickable models, non-empty only in 'any' mode */
  modelOptions: ModelOption[] = [];
  /** the key the user last picked in 'any' mode */
  selectedModel = '';
  /** the single model key to send in every mode except Multi */
  fixedModel = DEFAULT_SCAN_MODEL;
  /** the model keys to try in Multi mode */
  multiModels: string[] = [];

  /**
   * Read the shared scanner config and apply it. Call this once the page's view
   * exists; it awaits a database read.
   */
  async initAsync(configDb: ConfigDbService, settings: SettingsService) {
    // unified scanner model selection (#3783): every scanner reads this one key.
    const configValue = await configDb.getConfigValueAsync<string>(ConfigKey.ScanProvider);
    const config = parseScanConfig(configValue, DEFAULT_SCAN_MODEL, settings.scanModel);
    this.scanMode = config.scanMode;
    this.fixedModel = resolveModelKey(config);
    this.multiModels = config.multiModels;
    this.modelOptions = config.modelOptions;
    this.selectedModel = config.selectedModel;
  }

  /**
   * Apply the key the shared `cs-scanner-model-picker` emitted, and persist it to
   * the one shared setting so the choice carries across every scanner (#3783).
   * The caller repaints — this object knows nothing about change detection.
   */
  onModelSelected(key: string, settings: SettingsService) {
    this.selectedModel = key;
    this.fixedModel = key;
    settings.scanModel = key;
    settings.saveInBackground('scanner model selection');
  }
}
